import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useTheme } from '../utils/theme-context';
import TableLayout from '../components/TableLayout';

const Multiplayer = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { theme } = useTheme();
  const [playerCount, setPlayerCount] = useState(2);
  const [selectedFriends, setSelectedFriends] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState('');
  const [gameState, setGameState] = useState('setup'); // setup, game

  // Import subjects data
  const subjectsData = [
    "Mathematics", "Science", "English", "History", "Geography",
    "Computer Science", "Physics", "Chemistry", "Biology",
    "Economics", "Literature", "Art", "Music", "Sports",
    "Technology", "Languages", "Social Studies", "Psychology"
  ];

  const handlePlayerCountChange = (count) => {
    setPlayerCount(count);
    setSelectedFriends([]); // Reset selected friends
  };

  const handleFriendSelect = (index, friend) => {
    // Don't allow selecting more friends than needed
    const currentSelectedCount = selectedFriends.filter(f => f).length;
    const maxFriendsNeeded = playerCount - 1;
    
    if (currentSelectedCount >= maxFriendsNeeded && !selectedFriends[index]) {
      // Don't add more friends if we already have the maximum
      return;
    }
    
    const newSelectedFriends = [...selectedFriends];
    newSelectedFriends[index] = friend;
    setSelectedFriends(newSelectedFriends);
  };

  const startGame = () => {
    // Current user is 1 player, so we need (playerCount - 1) additional friends
    const requiredFriends = playerCount - 1;
    const selectedFriendsCount = selectedFriends.filter(f => f).length;
    
    if (selectedFriendsCount === requiredFriends && selectedSubject) {
      // Navigate to game room with all the data
      navigate('/game-room', { 
        state: { 
          playerCount, 
          selectedFriends: selectedFriends.filter(f => f), 
          selectedSubject 
        } 
      });
    }
  };

  const getTableLayout = () => {
    return 'square';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      <div className="container mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-8"
        >
          <h1 className="text-4xl font-bold text-gray-800 dark:text-gray-200 mb-4">
            🎮 Multiplayer Quiz
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-400">
            Challenge your friends in a fastest finger first quiz!
          </p>
        </motion.div>

        <AnimatePresence mode="wait">
          {gameState === 'setup' && (
            <motion.div
              key="setup"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.3 }}
              className="max-w-2xl mx-auto"
            >
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Player Count Selection */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 p-8">
                  <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-200 mb-6">
                    How many players?
                  </h2>
                  
                  <div className="flex items-center justify-center gap-4 mb-6">
                    <button
                      onClick={() => handlePlayerCountChange(Math.max(2, playerCount - 1))}
                      disabled={playerCount <= 2}
                      className={`w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold transition-colors ${
                        playerCount <= 2
                          ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                          : 'bg-blue-500 hover:bg-blue-600 text-white'
                      }`}
                    >
                      -
                    </button>
                    
                    <div className="text-4xl font-bold text-gray-800 dark:text-gray-200 min-w-[4rem] text-center">
                      {playerCount}
                    </div>
                    
                    <button
                      onClick={() => handlePlayerCountChange(Math.min(5, playerCount + 1))}
                      disabled={playerCount >= 5}
                      className={`w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold transition-colors ${
                        playerCount >= 5
                          ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                          : 'bg-blue-500 hover:bg-blue-600 text-white'
                      }`}
                    >
                      +
                    </button>
                  </div>
                  
                  <p className="text-sm text-gray-600 dark:text-gray-400 text-center">
                    Minimum: 2 players | Maximum: 5 players
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 text-center mt-2">
                    (You + {playerCount - 1} friends = {playerCount} total players)
                  </p>
                  
                  {/* Subject Selection */}
                  <div className="mt-8">
                    <h3 className="text-xl font-bold text-gray-800 dark:text-gray-200 mb-4">
                      Select Topic/Subject
                    </h3>
                    
                    <select
                      value={selectedSubject}
                      onChange={(e) => setSelectedSubject(e.target.value)}
                      className="w-full p-3 border-2 border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 focus:border-blue-500 focus:outline-none transition-colors"
                    >
                      <option value="">Choose a subject...</option>
                      {subjectsData.map((subject) => (
                        <option key={subject} value={subject}>
                          {subject}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Table Preview */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 p-8">
                  <div className="mb-4">
                    <TableLayout 
                      playerCount={playerCount} 
                      layout="preview" 
                      selectedFriends={selectedFriends}
                      onFriendSelect={handleFriendSelect}
                    />
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 text-center">
                    Click on the + signs to add friends to each position
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 text-center mt-1">
                    You'll be one of the {playerCount} players
                  </p>
                </div>
              </div>

              <motion.button
                onClick={startGame}
                disabled={selectedFriends.filter(f => f).length !== playerCount - 1 || !selectedSubject}
                className={`mt-8 w-full font-bold py-4 px-8 rounded-xl shadow-lg transition-all duration-300 transform ${
                  selectedFriends.filter(f => f).length === playerCount - 1 && selectedSubject
                    ? 'bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white hover:scale-105'
                    : 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                }`}
                whileHover={selectedFriends.filter(f => f).length === playerCount - 1 && selectedSubject ? { scale: 1.05 } : {}}
                whileTap={selectedFriends.filter(f => f).length === playerCount - 1 && selectedSubject ? { scale: 0.95 } : {}}
              >
                Start Game ({selectedFriends.filter(f => f).length}/{playerCount - 1} friends selected)
                {!selectedSubject && ' - Select a subject first'}
              </motion.button>
            </motion.div>
          )}




        </AnimatePresence>
      </div>
    </div>
  );
};

export default Multiplayer; 